import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllMembers,
  NewAddMember,
  toggleMemberStatus,
  UpdateMember,
} from "../../Redux/Slice/AddMember";
import DynamicTable from "../../Components/Table/DynamicTable";
import { BiSolidShow, BiSolidHide } from "react-icons/bi";
import Loader from "../../Components/Loader/Loader";

const AddMember = () => {
  const dispatch = useDispatch();
  const loginData = Cookies.get("loginData");
  const parsedData = loginData ? JSON.parse(loginData) : null;
  const userRole = parsedData?.data?.Role?.toLowerCase() || "";
  const { MemberData } = useSelector((state) => state.AddMemberSlice);

  const [formValues, setFormValues] = useState({
    Name: "",
    Role: "",
    Email: "",
    MobileNumber: "",
    Address: "",
    Password: "",
    Salary: "",
    CheckInTime: "10:00",
    CheckOutTime: "19:00",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Working Schedule Modal States
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [tempCheckIn, setTempCheckIn] = useState("10:00");
  const [tempCheckOut, setTempCheckOut] = useState("19:00");

  const roleOptions = [
    { value: "owner", label: "Owner" },
    { value: "management", label: "Management" },
    { value: "Delphi Senior Developer", label: "Delphi Senior Developer" },
    { value: "Delphi Junior Developer", label: "Delphi Junior Developer" },
    { value: "Web Senior Developer", label: "Web Senior Developer" },
    { value: "Web Junior Developer", label: "Web Junior Developer" },
    { value: "Senior Customer Support", label: "Senior Customer Support" },
    { value: "Junior Customer Support", label: "Junior Customer Support" },
    { value: "Data Analyst", label: "Data Analyst" },
    { value: "inhouse", label: "Inhouse" },
  ];

  const filteredOptions = roleOptions.filter((role) => {
    if (userRole === "owner") {
      return true;
    }

    if (userRole === "management") {
      return role.value !== "owner" && role.value !== "management";
    }

    if (
      userRole === "delphi senior developer" ||
      userRole === "web senior developer" ||
      userRole === "senior customer support"
    ) {
      return role.value !== "owner" && role.value !== "management";
    }

    // Default → allow all
    return true;
  });

  useEffect(() => {
    dispatch(fetchAllMembers());
  }, [dispatch]);

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64 = await convertToBase64(file);
        setPhoto(base64);
        setPhotoFile(file);
      } catch (error) { }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const { Name, Role, MobileNumber, Email, Address, Password, JoiningDate, Salary } =
      formValues;
    const newErrors = {};
    if (!Name) newErrors.Name = "Name is required";
    if (!Role) newErrors.Role = "Role is required";
    if (!MobileNumber) newErrors.MobileNumber = "Mobile number is required";
    else if (!/^[0-9]{10}$/.test(MobileNumber))
      newErrors.MobileNumber = "Must be 10 digits";
    if (!Email) newErrors.Email = "Email is required";
    if (!Address) newErrors.Address = "Address is required";
    if (!Password) newErrors.Password = "Password is required";
    if (!photoFile && !editing) newErrors.ProfilePhoto = "Image is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const Data = { ...formValues, ProfilePhoto: photo || "" };

    if (editing) {
      dispatch(UpdateMember({ id: editingId, data: Data })).then(() => {
        dispatch(fetchAllMembers());
        resetForm();
      });
    } else {
      dispatch(NewAddMember(Data)).then(() => {
        dispatch(fetchAllMembers());
        resetForm();
      });
    }
  };

  const resetForm = () => {
    setFormValues({
      Name: "",
      Role: "",
      MobileNumber: "",
      Email: "",
      Address: "",
      Password: "",
      Salary: "",
      CheckInTime: "10:00",
      CheckOutTime: "19:00",
    });
    setPhoto(null);
    setPhotoFile(null);
    setEditing(false);
    setEditingId(null);
    setErrors({});
  };

  const handleEdit = (memberData) => {

    setEditing(true);
    setEditingId(memberData.MainId);

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
    };

    setFormValues({
      Name: memberData.MemberName || "",
      Role: memberData.Role || memberData.Role.toLowerCase() || "",
      MobileNumber: memberData.MobileNumber || "",
      Email: memberData.Email || "",
      Address: memberData.Address || "",
      Password: memberData.Password || "",
      ProfilePhoto: memberData.ProfilePhoto || "",
      Salary: memberData.Salary || "",
      CheckInTime: memberData.CheckInTime || "10:00",
      CheckOutTime: memberData.CheckOutTime || "19:00",
    });
    setPhoto(memberData.ProfilePhoto);
    setPhotoFile(null);
    setErrors({});
  };

  const handleStatusToggle = (id) => {
    dispatch(toggleMemberStatus(id));
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "MemberName", headerName: "Member Name", flex: 1 },
    { field: "MobileNumber", headerName: "Mobile Number", flex: 1 },
    { field: "Role", headerName: "Role", flex: 1 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <button
          onClick={() => handleEdit(params.row)}
          className="py-2 px-3 text-white rounded-md bg-[#122A47] text-sm"
        >
          Edit
        </button>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <button
          className="btn px-3 py-2 rounded-md text-sm"
          style={{
            backgroundColor: params.row.Profile === "On" ? "blue" : "red",
            color: "white",
          }}
          onClick={() => handleStatusToggle(params.row.MainId)}
        >
          {params.row.Profile}
        </button>
      ),
    },
  ];

  const rows =
    MemberData?.data
      ?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        MobileNumber: el.MobileNumber,
        MemberName: el.Name,
        Email: el.Email,
        Password: el.Password,
        Role: el.Role,
        Address: el.Address,
        ProfilePhoto: el.ProfilePhoto,
        Profile: el.Profile,
        Salary: Array.isArray(el.Salary) ? "" : el.Salary || "",
        CheckInTime: el.CheckInTime,
        CheckOutTime: el.CheckOutTime,
      }))
      .filter(
        (row) =>
          row.MemberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.MobileNumber?.toString().includes(searchTerm) ||
          row.Role?.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [];

  if (!MemberData) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen p-4 md:p-6 bg-white">
      <h1 className="text-xl md:text-2xl font-bold bg-[#122A47] text-white px-4 py-2 inline-block rounded">
        ADD MEMBER
      </h1>

      {/* Form Section */}
      <form
        onSubmit={handleSubmit}
        className="mt-6 bg-gray-100 border border-gray-300 rounded-md p-4 grid grid-cols-1 md:grid-cols-12 gap-4"
      >
        {/* Upload Photo */}
        <div className="md:col-span-3 flex justify-center items-center border border-gray-300 rounded bg-slate-100 relative h-[250px]">
          {photo ? (
            <label className="cursor-pointer w-full h-full flex items-center justify-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
              <img
                src={photo}
                alt="Uploaded"
                className="max-h-full max-w-full object-contain rounded-sm"
              />
            </label>
          ) : (
            <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full p-4 rounded">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
              <span className="text-2xl">⬆️</span>
              <p className="text-sm mt-2 text-gray-500">Upload</p>
            </label>
          )}
          {errors.ProfilePhoto && (
            <p className="absolute bottom-2 text-red-600 text-sm">
              {errors.ProfilePhoto}
            </p>
          )}
        </div>

        {/* Fields */}
        <div className="md:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-bold mb-1">Full Name</label>
            <input
              name="Name"
              value={formValues.Name}
              onChange={handleChange}
              className="bg-[#F3F8FB] w-full border-2 border-black p-2 rounded"
              placeholder="Enter full name"
            />
            {errors.Name && (
              <p className="text-red-600 text-sm">{errors.Name}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-bold mb-1">Role</label>
            <select
              name="Role"
              value={formValues.Role}
              onChange={handleChange}
              className="bg-[#F3F8FB] w-full border-2 border-black p-2 rounded"
            >
              <option value="">Select Role</option>
              {filteredOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            {errors.Role && (
              <p className="text-red-600 text-sm">{errors.Role}</p>
            )}
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm font-bold mb-1">
              Mobile Number
            </label>
            <input
              name="MobileNumber"
              type="number"
              value={formValues.MobileNumber}
              onChange={handleChange}
              className="bg-[#F3F8FB] w-full border-2 border-black p-2 rounded"
              placeholder="Enter mobile number"
            />
            {errors.MobileNumber && (
              <p className="text-red-600 text-sm">{errors.MobileNumber}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-bold mb-1">Email</label>
            <input
              name="Email"
              type="email"
              value={formValues.Email}
              onChange={handleChange}
              className="bg-[#F3F8FB] w-full border-2 border-black p-2 rounded"
              placeholder="Enter email"
            />
            {errors.Email && (
              <p className="text-red-600 text-sm">{errors.Email}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-bold mb-1">Address</label>
            <input
              name="Address"
              value={formValues.Address}
              onChange={handleChange}
              className="bg-[#F3F8FB] w-full border-2 border-black p-2 rounded"
              placeholder="Enter address"
            />
            {errors.Address && (
              <p className="text-red-600 text-sm">{errors.Address}</p>
            )}
          </div>

          {/* Password */}
          <div className="relative">
            <label className="block text-sm font-bold mb-1">Password</label>
            <input
              name="Password"
              type={showPassword ? "text" : "password"}
              value={formValues.Password}
              onChange={handleChange}
              className="bg-[#F3F8FB] w-full border-2 border-black p-2 rounded pr-10"
              placeholder="Enter password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-8 text-gray-600"
            >
              {showPassword ? (
                <BiSolidHide size={18} />
              ) : (
                <BiSolidShow size={18} />
              )}
            </button>
            {errors.Password && (
              <p className="text-red-600 text-sm">{errors.Password}</p>
            )}
          </div>

          {/* Salary */}
          <div>
            <label className="block text-sm font-bold mb-1">Salary</label>
            <input
              name="Salary"
              value={formValues.Salary}
              onChange={handleChange}
              className="bg-[#F3F8FB] w-full border-2 border-black p-2 rounded"
              placeholder="Enter salary"
            />
            {errors.Salary && (
              <p className="text-red-600 text-sm">{errors.Salary}</p>
            )}
          </div>

          {/* Working Schedule */}
          <div>
            <label className="block text-sm font-bold mb-1">Working Schedule</label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={`${formValues.CheckInTime || "10:00"} to ${formValues.CheckOutTime || "19:00"}`}
                className="bg-[#E5E7EB] w-full border-2 border-black p-2 rounded cursor-not-allowed font-semibold text-slate-700 h-10"
              />
              <button
                type="button"
                onClick={() => {
                  setTempCheckIn(formValues.CheckInTime || "10:00");
                  setTempCheckOut(formValues.CheckOutTime || "19:00");
                  setIsScheduleModalOpen(true);
                }}
                className="bg-[#122A47] hover:bg-[#0c2545] text-white px-4 rounded font-bold text-xl flex items-center justify-center cursor-pointer transition-all shadow-sm h-10"
                title="Define Schedule"
              >
                +
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-end justify-end col-span-1 md:col-span-2">
            <button
              type="submit"
              className="bg-[#122A47] text-white py-2 px-4 rounded hover:bg-[#0c2545]"
            >
              {editing ? "UPDATE MEMBER" : "ADD MEMBER"}
            </button>
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className="ml-2 bg-gray-400 text-white py-2 px-4 rounded hover:bg-gray-600"
              >
                CANCEL
              </button>
            )}
          </div>
        </div>
      </form>

      {/* ✅ Dynamic Table with built-in search */}
      <div className="my-6">
        <DynamicTable
          rows={rows}
          columns={columns}
          searchValue={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* ⏰ Working Schedule Definition Modal */}
      {isScheduleModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 transition-all duration-300">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 flex flex-col shadow-2xl border border-slate-100 transform scale-100 transition-all duration-300">
            {/* Header */}
            <div className="border-b border-slate-150 pb-3 mb-4">
              <h3 className="text-base font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
                ⏰ Define Working Schedule
              </h3>
            </div>

            {/* Body */}
            <div className="space-y-4 py-2">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Check-In Time (Start Hour)
                </label>
                <input
                  type="time"
                  value={tempCheckIn}
                  onChange={(e) => setTempCheckIn(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border-2 border-slate-200 focus:border-indigo-500 rounded-xl font-bold text-slate-800 text-sm focus:outline-none transition-all cursor-pointer"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Check-Out Time (End Hour)
                </label>
                <input
                  type="time"
                  value={tempCheckOut}
                  onChange={(e) => setTempCheckOut(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border-2 border-slate-200 focus:border-indigo-500 rounded-xl font-bold text-slate-800 text-sm focus:outline-none transition-all cursor-pointer"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setIsScheduleModalOpen(false)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-600 hover:text-slate-800 hover:bg-slate-50 text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormValues((prev) => ({
                    ...prev,
                    CheckInTime: tempCheckIn,
                    CheckOutTime: tempCheckOut,
                  }));
                  setIsScheduleModalOpen(false);
                }}
                className="px-4 py-2 bg-[#122A47] hover:bg-[#0c2545] text-white text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer"
              >
                Save Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddMember;
